import React, { useState, useEffect, useRef } from 'react';
import { storyAPI } from '../../../config/story-api';
import { Plus, Edit, Trash2, GripVertical, Save, X, ArrowUp, ArrowDown, Upload, Image as ImageIcon, Play, ChevronDown, ChevronRight, Video, RotateCcw, ArrowUpIcon, ArrowDownIcon, Loader2, RefreshCcw, Eye, Share2 } from 'lucide-react';
import './Stories.css';
import { toast } from 'react-toastify';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';
import { productAPI } from '../../../config/product-api';
import { IS_ALFAJR } from '../../../constants/general';
import ProductSelectionModal from '../../Common/ProductSelectionModal/ProductSelectionModal';

const mb = (bytes) => (bytes / 1024 / 1024).toFixed(2);
const formatCompressionSize = (bytes) => bytes >= 1024 * 1024
  ? `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  : `${(bytes / 1024).toFixed(1)} KB`;
const formatDuration = (seconds) => `${Number(seconds || 0).toFixed(1)}s`;
const logCompressionResult = (scope, file, finalFile, status, reason = '', durationSec = null) => {
  const savedPct = file.size > 0 ? ((1 - (finalFile.size / file.size)) * 100).toFixed(1) : '0.0';
  console.log(`[${scope}] | original: ${formatCompressionSize(file.size)} | final: ${formatCompressionSize(finalFile.size)} | saved: ${savedPct}% | duration: ${durationSec !== null ? formatDuration(durationSec) : 'n/a'} | status: ${status}${reason ? ` | reason: ${reason}` : ''} | file: ${file.name}`);
};
const logCompressionSkip = (scope, file, reason) => {
  console.log(`[${scope}] | original: ${formatCompressionSize(file.size)} | final: ${formatCompressionSize(file.size)} | saved: 0.0% | duration: n/a | status: original kept | reason: ${reason} | file: ${file.name}`);
};

const Stories = ({ adminData }) => {
  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_STORIES);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_STORIES);
  const canDelete = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.DELETE_STORIES);

  const [activeStories, setActiveStories] = useState([]);
  const [expiredStories, setExpiredStories] = useState([]);
  const [isExpiredExpanded, setIsExpiredExpanded] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isExpiredLoading, setIsExpiredLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeCount, setActiveCount] = useState(0);
  const [expiredCount, setExpiredCount] = useState(0);

  const [isReorderMode, setIsReorderMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedStories, setExpandedStories] = useState({});
  const [showStoryDialog, setShowStoryDialog] = useState(false);
  const [showMediaDialog, setShowMediaDialog] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [currentStoryId, setCurrentStoryId] = useState(null);
  const [editingCollection, setEditingCollection] = useState(null);
  const [originalStories, setOriginalStories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const storiesPerPage = 20;
  const [storyForm, setStoryForm] = useState({
    label: '',
    thumbnail: null,
    thumbnailPreview: null,
    expiryDate: '',
    expiryTime: ''
  });
  const [mediaForm, setMediaForm] = useState({
    file: null,
    preview: null,
    type: 'image',
    productIds: []
  });

  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [isProductViewDialogOpen, setIsProductViewDialogOpen] = useState(false);
  const [selectedProductsInfo, setSelectedProductsInfo] = useState([]);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [deleteStoryId, setDeleteStoryId] = useState(null);
  const [deleteCollectionId, setDeleteCollectionId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [isCreatingStory, setIsCreatingStory] = useState(false);
  const [isUpdatingStory, setIsUpdatingStory] = useState(false);
  const [isAddingCollection, setIsAddingCollection] = useState(false);
  const [isUpdatingCollection, setIsUpdatingCollection] = useState(false);
  const [repostingId, setRepostingId] = useState(null);

  const ffmpegRef = useRef(null);
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
  const [compressingVideo, setCompressingVideo] = useState(false);
  const isCancelledRef = useRef(false);

  useEffect(() => {
    fetchStories(currentPage);
  }, []);

  useEffect(() => {
    return () => {
      try {
        ffmpegRef.current?.terminate?.();
      } catch { }
    };
  }, []);

  useEffect(() => {
    if (isExpiredExpanded) {
      fetchExpiredStories(currentPage);
    }
  }, [isExpiredExpanded, currentPage]);

  const getDefaultExpiry = () => {
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    return {
      expiryDate: d.toISOString().slice(0, 10),         // "YYYY-MM-DD"
      expiryTime: d.toTimeString().slice(0, 5)          // "HH:mm"
    };
  };

  const fetchStories = async (page = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const activeResult = await storyAPI.fetchActiveStories();

      if (activeResult.success) {
        const formatStory = (story) => ({
          ...story,
          id: story._id,
          thumbnail: story.image,
          collections: (story.collections || []).map(col => ({
            ...col,
            id: col._id,
            products: col.products || []
          }))
        });

        setActiveStories((activeResult.data || []).map(formatStory));
        setActiveCount(activeResult.activeCount || activeResult.data?.length || 0);
        setExpiredCount(activeResult.expiredCount || 0);
      } else {
        setError('Failed to fetch active stories');
        setActiveStories([]);
      }
    } catch (err) {
      console.error('Error fetching stories:', err);
      setError('An error occurred while fetching stories');
      setActiveStories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExpiredStories = async (page = 1) => {
    setIsExpiredLoading(true);

    try {
      const expiredResult = await storyAPI.fetchExpiredStories(page, storiesPerPage);

      if (expiredResult.success) {
        const formatStory = (story) => ({
          ...story,
          id: story._id,
          thumbnail: story.image,
          collections: (story.collections || []).map(col => ({
            ...col,
            id: col._id,
            products: col.products || []
          }))
        });

        setExpiredStories((expiredResult.data.stories || []).map(formatStory));
        setPagination(expiredResult.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          hasNext: false,
          hasPrev: false
        });
      }
    } catch (err) {
      console.error('Error fetching expired stories:', err);
    } finally {
      setIsExpiredLoading(false);
    }
  };

  const fetchProductDetails = async (productIds) => {
    if (!productIds || productIds.length === 0) return;
    try {
      const productInfos = [];
      for (const pid of productIds) {
        const response = await productAPI.fetchProducts(1, pid, 'all', 'all', 'all', 1);
        if (response.success && response.data.products && response.data.products.length > 0) {
          productInfos.push(response.data.products[0]);
        }
      }
      setSelectedProductsInfo(productInfos);
    } catch (err) {
      console.error('Error fetching product details:', err);
    }
  };

  const handleProductSave = (ids) => {
    const productIds = (ids || []).map((id) => String(id));
    setMediaForm((prev) => ({ ...prev, productIds }));
    if (productIds.length > 0) {
      fetchProductDetails(productIds);
    } else {
      setSelectedProductsInfo([]);
    }
    setIsProductDialogOpen(false);
  };

  const removeSelectedProduct = () => {
    setMediaForm((prev) => ({ ...prev, productIds: [] }));
    setSelectedProductsInfo([]);
    setIsProductViewDialogOpen(false);
  };

  const selectedProduct = selectedProductsInfo[0] || null;

  const toggleReorderMode = () => {
    if (isReorderMode) {
      if (hasUnsavedChanges) {
        setActiveStories([...originalStories]);
      }
      setIsReorderMode(false);
      setHasUnsavedChanges(false);
      setExpandedStories({});
    } else {
      setOriginalStories([...activeStories]);
      setIsReorderMode(true);
      const allExpanded = activeStories.reduce((acc, story) => {
        acc[story.id] = true;
        return acc;
      }, {});
      setExpandedStories(allExpanded);
    }
  };

  const toggleStory = (storyId) => {
    setExpandedStories(prev => ({
      ...prev,
      [storyId]: !prev[storyId]
    }));
  };

  const moveStory = (storyId, direction) => {
    const currentIndex = activeStories.findIndex(story => story.id === storyId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= activeStories.length) return;

    const newStories = [...activeStories];
    const [movedStory] = newStories.splice(currentIndex, 1);
    newStories.splice(newIndex, 0, movedStory);

    const updatedStories = newStories.map((story, index) => ({
      ...story,
      order: index + 1
    }));

    setActiveStories(updatedStories);
    setHasUnsavedChanges(true);
  };

  const moveCollection = (collectionId, storyId, direction) => {
    const story = activeStories.find(s => s.id === storyId);
    if (!story) return;

    const currentIndex = story.collections.findIndex(c => c.id === collectionId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= story.collections.length) return;

    const newCollections = [...story.collections];
    const [movedCollection] = newCollections.splice(currentIndex, 1);
    newCollections.splice(newIndex, 0, movedCollection);

    const updatedCollections = newCollections.map((collection, index) => ({
      ...collection,
      order: index + 1
    }));

    const updatedStories = activeStories.map(s =>
      s.id === storyId
        ? { ...s, collections: updatedCollections }
        : s
    );

    setActiveStories(updatedStories);
    setHasUnsavedChanges(true);
  };


  const handleSaveChanges = async () => {
    setIsSaving(true);

    try {
      const reorderPayload = {
        stories: activeStories.map((story) => ({
          _id: story.id,
          newOrder: story.order,
          collections: (story.collections || []).map((collection) => ({
            _id: collection.id,
            newOrder: collection.order
          }))
        }))
      };

      console.log('Saving story order:', reorderPayload.stories.map((story) => ({
        id: story._id,
        order: story.newOrder,
        collections: story.collections.length
      })));

      const response = await storyAPI.reorderStories(reorderPayload);

      if (!response.success) {
        throw new Error(response.error || 'Failed to save story order');
      }

      await fetchStories(currentPage);
      setOriginalStories([...activeStories]);
      setHasUnsavedChanges(false);
      toast.success(response.msg || 'Story order saved successfully!');
      setIsReorderMode(false);
      setExpandedStories({});
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error(error.message || 'Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateStory = () => {
    setEditingStory(null);
    setStoryForm({
      label: '',
      thumbnail: null,
      thumbnailPreview: null,
      ...getDefaultExpiry()
    });
    setShowStoryDialog(true);
  };

  const handleAddMedia = (storyId) => {
    setCurrentStoryId(storyId);
    setEditingCollection(null);
    setMediaForm({
      file: null,
      preview: null,
      type: 'image',
      productIds: []
    });
    setSelectedProductsInfo([]);
    setShowMediaDialog(true);
  };

  const handleEditCollection = (storyId, collection) => {
    setCurrentStoryId(storyId);
    setEditingCollection(collection);
    const productIds = (collection.products || []).map(p => p.productId);
    setMediaForm({
      file: null,
      preview: collection.url,
      type: collection.type,
      productIds: productIds
    });

    if (productIds.length > 0) {
      const allHaveNames = (collection.products || []).every(p => p.name);
      if (allHaveNames) {
        setSelectedProductsInfo(collection.products);
      } else {
        fetchProductDetails(productIds);
      }
    } else {
      setSelectedProductsInfo([]);
    }

    setShowMediaDialog(true);
  };

  const handleEditStory = (storyId) => {
    const story = [...activeStories, ...expiredStories].find(s => s.id === storyId);
    if (story) {
      setEditingStory(story);

      let dateForInput = "";
      let timeForInput = "";

      if (story.expiryAt && story.expiryAt !== 'N/A') {
        const parts = story.expiryAt.split(' ');
        if (parts.length >= 3) {
          const [day, month, year] = parts[0].split('-');
          dateForInput = `${year}-${month}-${day}`;

          let [hours, minutes] = parts[1].split(':');
          let hour24 = parseInt(hours, 10);
          const ampm = parts[2].toUpperCase();

          if (ampm === 'PM' && hour24 !== 12) {
            hour24 += 12;
          } else if (ampm === 'AM' && hour24 === 12) {
            hour24 = 0;
          }
          timeForInput = `${String(hour24).padStart(2, '0')}:${minutes}`;
        }
      }

      if (!dateForInput || !timeForInput) {
        const existingExpiry = new Date(Date.now() + 24 * 60 * 60 * 1000);
        dateForInput = existingExpiry.toISOString().slice(0, 10);
        timeForInput = existingExpiry.toTimeString().slice(0, 5);
      }

      setStoryForm({
        label: story.label,
        thumbnail: null,
        thumbnailPreview: story.thumbnail,
        expiryDate: dateForInput,
        expiryTime: timeForInput
      });
      setShowStoryDialog(true);
    }
  };

  const compressImage = (file, targetKbRange) => {
    const startedAt = Date.now();
    const sizeKB = file.size / 1024;

    if (sizeKB < 50) {
      logCompressionSkip('Story Image', file, 'limit guard: below 50 KB threshold');
      return Promise.resolve(file);
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;

          // Downscale slightly if extreme to help reach targetKB
          const maxDim = 1200;
          if (width > maxDim || height > maxDim) {
            const ratio = Math.min(maxDim / width, maxDim / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          let minQ = 0.3, maxQ = 0.95, q = 0.75;
          let attempts = 0;

          const outType = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/webp';

          const iterate = () => {
            canvas.toBlob((blob) => {
              attempts++;
              const kb_res = blob.size / 1024;

              if ((kb_res >= targetKbRange[0] && kb_res <= targetKbRange[1]) || attempts >= 5) {
                return finish(blob);
              }

              if (kb_res > targetKbRange[1]) {
                maxQ = q;
                q = (minQ + maxQ) / 2;
                iterate();
              } else {
                minQ = q;
                q = (minQ + maxQ) / 2;
                iterate();
              }
            }, outType, q);
          };

          const finish = (blob) => {
            if (blob.size < file.size) {
              const newFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + (outType === 'image/webp' ? '.webp' : '.jpg'), {
                type: outType,
                lastModified: Date.now(),
              });
              logCompressionResult('Story Image', file, newFile, 'compressed', `iterations: ${attempts}`, (Date.now() - startedAt) / 1000);
              resolve(newFile);
            } else {
              logCompressionResult('Story Image', file, file, 'original kept', `already optimized; iterations: ${attempts}`, (Date.now() - startedAt) / 1000);
              resolve(file);
            }
          };

          iterate();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        toast.error('Image exceeds the 1MB limit');
        return;
      }

      let finalFile = file;

      try {
        let targetRange;
        if (file.size > 200 * 1024) {
          targetRange = [150, 190]; // Force below 200KB
        } else {
          targetRange = [100, 150]; // Small compression
        }
        finalFile = await compressImage(file, targetRange);
      } catch (err) {
        console.error('Image compression failed', err);
      }

      const reader = new FileReader();
      reader.onload = (ev) => {
        setStoryForm(prev => ({
          ...prev,
          thumbnail: finalFile,
          thumbnailPreview: ev.target.result
        }));
      };
      reader.readAsDataURL(finalFile);
    }
  };

  const loadFFmpegIfNeeded = async () => {
    if (ffmpegRef.current?.loaded) return;

    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const { toBlobURL } = await import('@ffmpeg/util');

    ffmpegRef.current = new FFmpeg();
    const ffmpeg = ffmpegRef.current;

    let lastMilestone = 0;
    ffmpeg.on('progress', ({ progress }) => {
      const pct = Math.floor(progress * 100);
      if (pct >= lastMilestone + 25) {
        lastMilestone = Math.floor(pct / 25) * 25;
        console.log(`[Story Video] Compressing... ${lastMilestone}%`);
      }
    });

    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';

    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });

    setFfmpegLoaded(true);
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(video.duration || 10);
      };
      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        resolve(10);
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const compressVideo = async (videoFile) => {
    const startedAt = Date.now();
    const { fetchFile } = await import('@ffmpeg/util');
    const ffmpeg = ffmpegRef.current;

    const input = `in_${Date.now()}.mp4`;
    const output = `out_${Date.now()}.mp4`;

    const duration = await getVideoDuration(videoFile);
    await ffmpeg.writeFile(input, await fetchFile(videoFile));

    const audioBitrateKbps = 96;
    const fileSizeMB = videoFile.size / (1024 * 1024);

    let targetMB;
    if (fileSizeMB > 5) {
      targetMB = 2.5;
    } else if (fileSizeMB > 3) {
      targetMB = 1.8;
    } else if (fileSizeMB > 1) {
      targetMB = 1.0;
    } else {
      targetMB = 0.6;
    }

    const targetBytes = targetMB * 1024 * 1024;
    const totalBitrateKbps = Math.floor((targetBytes * 8) / duration / 1000);
    const videoBitrateKbps = Math.max(300, totalBitrateKbps - audioBitrateKbps);

    console.log(`[Story Video] | original: ${formatCompressionSize(videoFile.size)} | final: pending | saved: pending | duration: ${duration.toFixed(1)}s | status: processing | reason: target ${targetMB.toFixed(2)} MB @ ${videoBitrateKbps}k | file: ${videoFile.name}`);

    const ffmpegArgs = [
      '-i', input,
      '-c:v', 'libx264',
      '-preset', 'superfast',
      '-b:v', `${videoBitrateKbps}k`,
      '-maxrate', `${Math.floor(videoBitrateKbps * 1.3)}k`,
      '-bufsize', `${videoBitrateKbps * 2}k`,
      '-vf', 'scale=trunc(iw/2)*2:trunc(ih/2)*2',
      '-pix_fmt', 'yuv420p',
      '-movflags', 'faststart',
      '-c:a', 'aac', '-b:a', `${audioBitrateKbps}k`,
      output
    ];

    await ffmpeg.exec(ffmpegArgs);

    const data = await ffmpeg.readFile(output);
    await ffmpeg.deleteFile(input);
    await ffmpeg.deleteFile(output);

    const blob = new Blob([data.buffer], { type: 'video/mp4' });
    const outputMB = blob.size / (1024 * 1024);
    const savings = ((1 - blob.size / videoFile.size) * 100).toFixed(1);
    const elapsedSec = ((Date.now() - startedAt) / 1000).toFixed(1);

    console.log(`[Story Video] | original: ${formatCompressionSize(videoFile.size)} | final: ${formatCompressionSize(blob.size)} | saved: ${savings}% | duration: ${elapsedSec}s | status: processed | file: ${videoFile.name}`);

    return blob;
  };

  const handleMediaChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const isVideo = file.type.startsWith('video/');

      if (!isVideo && file.size > 1 * 1024 * 1024) {
        toast.error('Image exceeds the 1MB limit');
        return;
      }
      if (isVideo && file.size > 6 * 1024 * 1024) {
        toast.error('Video exceeds the 6MB limit');
        return;
      }

      let finalFile = file;

      if (!isVideo) {
        if (file.size >= 200 * 1024) {
          try {
            let targetRange;
            if (file.size >= 700 * 1024) {
              targetRange = [400, 500];
            } else if (file.size >= 300 * 1024) {
              targetRange = [200, 300];
            } else {
              targetRange = [200, 250];
            }
            finalFile = await compressImage(file, targetRange);
          } catch (err) {
            console.error('Image compression failed', err);
          }
        }
      }

      if (isVideo) {
        // Always attempt video compression with tiered logic
        isCancelledRef.current = false;
        setCompressingVideo(true);

        try {
          await loadFFmpegIfNeeded();
          const compressionStartedAt = Date.now();
          const compressedBlob = await compressVideo(file);

          if (isCancelledRef.current) return;

          const compressedFile = new File(
            [compressedBlob],
            file.name.replace(/\.[^/.]+$/, '.mp4'),
            { type: 'video/mp4' }
          );

          if (compressedFile.size < file.size) {
            finalFile = compressedFile;
            logCompressionResult('Story Collection Video', file, finalFile, 'compressed', '', (Date.now() - compressionStartedAt) / 1000);
          } else {
            finalFile = file;
            logCompressionResult('Story Collection Video', file, finalFile, 'original kept', 'already optimized', (Date.now() - compressionStartedAt) / 1000);
          }
        } catch (error) {
          if (!isCancelledRef.current) {
            console.error('Video processing error:', error);
            toast.error('Video processing failed');
          }
        } finally {
          if (!isCancelledRef.current) {
            setCompressingVideo(false);
          }
        }
      }

      if (isCancelledRef.current) return;

      const reader = new FileReader();
      reader.onload = (ev) => {
        setMediaForm(prev => ({
          ...prev,
          file: finalFile,
          preview: ev.target.result,
          type: isVideo ? 'video' : 'image'
        }));
      };
      reader.readAsDataURL(finalFile);
    }
  };

  const handleSaveStory = async () => {
    if (!storyForm.label.trim()) {
      toast.error('Please fill in the story label');
      return;
    }

    if (storyForm.expiryDate && storyForm.expiryTime) {
      const selectedExpiry = new Date(`${storyForm.expiryDate}T${storyForm.expiryTime}`);
      if (selectedExpiry < new Date()) {
        toast.error('Expiry time cannot be in the past');
        return;
      }
    }

    if (editingStory) {
      setIsUpdatingStory(true);
    } else {
      setIsCreatingStory(true);
    }

    try {
      const formData = new FormData();
      formData.append('label', storyForm.label);

      if (storyForm.thumbnail) {
        formData.append('image', storyForm.thumbnail);
      }

      if (storyForm.expiryDate && storyForm.expiryTime) {
        formData.append('expiryDate', storyForm.expiryDate);
        formData.append('expiryTime', storyForm.expiryTime);
      }

      if (!editingStory) {
        formData.append('order', activeStories.length + expiredStories.length);
      }

      let result;
      if (editingStory) {
        result = await storyAPI.updateStory(editingStory.id, formData);
      } else {
        result = await storyAPI.createStory(formData);
      }

      if (result.success) {
        toast.success(editingStory ? 'Story updated successfully!' : 'Story created successfully!');
        await fetchStories(currentPage);
        if (isExpiredExpanded) fetchExpiredStories(currentPage);
        handleCloseDialog();
      } else {
        toast.error(result.error || 'Failed to save story');
      }
    } catch (error) {
      console.error('Error saving story:', error);
      toast.error('An error occurred while saving the story');
    } finally {
      setIsCreatingStory(false);
      setIsUpdatingStory(false);
    }
  };

  const handleSaveMedia = async () => {
    if (!mediaForm.file && !mediaForm.preview && !editingCollection) {
      toast.error('Please select a media file');
      return;
    }

    if (editingCollection) {
      setIsUpdatingCollection(true);
    } else {
      setIsAddingCollection(true);
    }

    try {
      let result;

      if (editingCollection) {
        const payload = {
          productId: mediaForm.productIds[0] || ''
        };

        result = await storyAPI.updateCollectionInStory(currentStoryId, editingCollection.id, payload);
      } else {
        const formData = new FormData();

        formData.append('type', mediaForm.type);

        if (mediaForm.productIds.length > 0) {
          formData.append('productId', mediaForm.productIds[0]);
        }

        if (mediaForm.file) {
          formData.append('file', mediaForm.file);
        }

        result = await storyAPI.addCollectionToStory(currentStoryId, formData);
      }

      if (result.success) {
        toast.success(editingCollection ? 'Collection updated successfully!' : 'Collection added successfully!');
        handleCloseMediaDialog();
        await fetchStories();
        if (isExpiredExpanded) fetchExpiredStories(currentPage);
      } else {
        toast.error(result.error || 'Failed to save collection');
      }
    } catch (error) {
      console.error('Error saving collection:', error);
      toast.error('An error occurred while saving the collection');
    } finally {
      setIsUpdatingCollection(false);
      setIsAddingCollection(false);
    }
  };

  const handleShareStory = (storyId) => {
    const domain = IS_ALFAJR ? 'fajr.qa' : 'ablemartonline.com';
    const url = `https://${domain}/stories/${storyId}`;
    navigator.clipboard.writeText(url)
      .then(() => {
        toast.success("Story link copied!");
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
        toast.error("Failed to copy link");
      });
  };

  const openDeleteCollectionConfirm = (storyId, collectionId) => {
    setDeleteType('collection');
    setDeleteStoryId(storyId);
    setDeleteCollectionId(collectionId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);

    try {
      let result;
      if (deleteType === 'story') {
        result = await storyAPI.deleteStory(deleteStoryId);
      } else if (deleteType === 'collection') {
        result = await storyAPI.deleteCollection(deleteStoryId, deleteCollectionId);
      }

      if (result.success) {
        toast.success('Deleted successfully!');
        fetchStories(currentPage);
        if (isExpiredExpanded) fetchExpiredStories(currentPage);
      } else {
        toast.error(result.error || 'Delete failed');
      }
    } catch (error) {
      toast.error('Error deleting item');
    } finally {
      setDeleteLoading(false);
      setShowDeleteDialog(false);
      setDeleteType(null);
      setDeleteStoryId(null);
      setDeleteCollectionId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteType(null);
    setDeleteStoryId(null);
    setDeleteCollectionId(null);
  };

  const handleCloseDialog = () => {
    setShowStoryDialog(false);
    setEditingStory(null);
    setStoryForm({ label: '', thumbnail: null, thumbnailPreview: null, expiryDate: '', expiryTime: '' });
  };

  const handleCloseMediaDialog = () => {
    if (compressingVideo) {
      isCancelledRef.current = true;
      try {
        ffmpegRef.current?.terminate?.();
      } catch { }
      ffmpegRef.current = null;
      setFfmpegLoaded(false);
      setCompressingVideo(false);
    }

    setShowMediaDialog(false);
    setCurrentStoryId(null);
    setEditingCollection(null);
    setMediaForm({ file: null, preview: null, type: 'image', productIds: [] });
    setSelectedProductsInfo([]);
    setIsProductDialogOpen(false);
    setIsProductViewDialogOpen(false);
  };

  const openDeleteStoryConfirm = (storyId) => {
    setDeleteType('story');
    setDeleteStoryId(storyId);
    setShowDeleteDialog(true);
  };

  const handleRepostStory = async (storyId) => {
    setRepostingId(storyId);
    try {
      const result = await storyAPI.repostStory(storyId);
      if (result.success) {
        toast.success('Story reposted successfully!');
        await fetchStories(currentPage);
        if (isExpiredExpanded) fetchExpiredStories(currentPage);
      } else {
        toast.error(result.error || 'Failed to repost story');
      }
    } catch (error) {
      console.error('Error reposting story:', error);
      toast.error('An error occurred while reposting the story');
    } finally {
      setRepostingId(null);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasNext) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (pagination.hasPrev) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleToggleExpired = () => {
    setIsExpiredExpanded(!isExpiredExpanded);

    if (!isExpiredExpanded) {
      setCurrentPage(1);
      fetchExpiredStories(1);
    } else {
      setExpiredStories([]);
    }
  };

  const handleRetryButton = async () => {
    await fetchStories(currentPage);

    if (isExpiredExpanded) {
      await fetchExpiredStories(currentPage);
    }
  };

  const renderStoryCard = (story, index, storiesArray) => (
    <div key={story.id} className={`stories-story-card ${isReorderMode ? 'stories-reorder-mode' : ''}`}>
      <div className="stories-story-header">
        {isReorderMode && !story.isExpired && (
          <div className="stories-reorder-controls">
            <button
              className="stories-move-btn"
              onClick={() => moveStory(story.id, 'up')}
              disabled={index === 0}
              title="Move up"
            >
              <ArrowUp size={16} />
            </button>
            <button
              className="stories-move-btn"
              onClick={() => moveStory(story.id, 'down')}
              disabled={index === storiesArray.length - 1}
              title="Move down"
            >
              <ArrowDown size={16} />
            </button>
          </div>
        )}

        <div className={`stories-story-thumbnail ${story.isExpired ? 'stories-expired' : ''}`}>
          <img src={story.thumbnail} alt={story.label} />
        </div>

        <div className="stories-story-info">
          <div className="stories-story-label-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <h3 className="stories-story-label" style={{ margin: 0 }}>{story.label}</h3>
            <button
              className="stories-title-share-btn"
              type="button"
              onClick={() => handleShareStory(story.id)}
              title="Copy link"
            >
              <Share2 size={14} />
            </button>
          </div>
          <div className="stories-story-meta">
            <div className="stories-story-date">
              <span className="stories-date-label">Created:</span>
              <span className="stories-date-value">{story.createdAt || '-'}</span>
            </div>
            <div className="stories-story-expiry-row">
              <div className="stories-story-expiry">
                <span className="stories-expiry-label">Expires:</span>
                <span className={`stories-expiry-value ${story.isExpired ? 'stories-expired' : ''}`}>
                  {story.timeRemaining}
                </span>
              </div>
              {story.maxViews > 0 && (
                <div className="stories-story-views">
                  <Eye size={14} className="stories-view-icon" />
                  <span className="stories-view-count">{story.maxViews}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {!isReorderMode && (
          <div className="stories-story-actions">
            <div className="stories-story-actions-line1">
              {story.isExpired && canEdit && (
                <button
                  className="stories-action-btn stories-repost-btn"
                  onClick={() => handleRepostStory(story.id)}
                  title="Repost"
                  disabled={repostingId === story.id}
                >
                  {repostingId === story.id ? (
                    <Loader2 size={16} className="stories-spinner" />
                  ) : (
                    <RotateCcw size={16} />
                  )}
                </button>
              )}
              {canEdit && (
                <button
                  className="stories-action-btn stories-add-media-btn"
                  onClick={() => handleAddMedia(story.id)}
                  title="Add Media"
                >
                  <Plus size={16} />
                </button>
              )}
            </div>
            <div className="stories-story-actions-line2">
              {canEdit && (
                <button
                  className="stories-action-btn stories-edit-btn"
                  onClick={() => handleEditStory(story.id)}
                  title="Edit"
                >
                  <Edit size={16} />
                </button>
              )}
              {canDelete && (
                <button
                  className="stories-action-btn stories-delete-btn"
                  onClick={() => openDeleteStoryConfirm(story.id)}
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {story.collections && story.collections.length > 0 && (
        <div className="stories-collections-section">
          {!isReorderMode && (
            <button
              className="stories-collections-toggle"
              onClick={() => toggleStory(story.id)}
            >
              <span className="stories-collections-label">
                Collections ({story.collections.length})
              </span>
              {expandedStories[story.id] ?
                <ChevronDown size={16} /> :
                <ChevronRight size={16} />
              }
            </button>
          )}

          {(isReorderMode || expandedStories[story.id]) && (
            <div className="stories-collections-list">
              {story.collections
                .sort((a, b) => a.order - b.order)
                .map((collection, colIndex) => (
                  <div key={collection.id} className={`stories-collection-item ${isReorderMode ? 'stories-reorder-mode' : ''}`}>
                    {isReorderMode && !story.isExpired && (
                      <div className="stories-reorder-controls">
                        <button
                          className="stories-move-btn"
                          onClick={() => moveCollection(collection.id, story.id, 'up')}
                          disabled={colIndex === 0}
                          title="Move up"
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          className="stories-move-btn"
                          onClick={() => moveCollection(collection.id, story.id, 'down')}
                          disabled={colIndex === story.collections.length - 1}
                          title="Move down"
                        >
                          <ArrowDown size={14} />
                        </button>
                      </div>
                    )}
                    <div className="stories-collection-thumbnail-large">
                      {collection.type === 'video' ? (
                        <div className="stories-video-thumbnail-large">
                          <Play size={20} />
                        </div>
                      ) : (
                        <img src={collection.url} alt="Collection item" />
                      )}
                    </div>
                    <div className="stories-collection-info">
                      <span className="stories-collection-type">
                        {collection.type === 'video' ? 'Video' : 'Image'}
                      </span>

                      {collection.totalViews > 0 && (
                        <div className="stories-collection-views">
                          <Eye size={12} className="stories-view-icon" />
                          <span className="stories-view-count">{collection.totalViews}</span>
                        </div>
                      )}

                      {collection.products && collection.products.length > 0 && (
                        <>
                          {collection.products[0]?.productId && collection.products.length == 1 && (
                            <span className="stories-collection-type">
                              ID: {collection.products[0].productId}
                            </span>)}

                          {collection.products.length > 1 && (
                            <span className="stories-collection-type">
                              {collection.products.length} product{collection.products.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {!isReorderMode && (
                      <div className="stories-collection-actions">
                        {canEdit && (
                          <button
                            className="stories-action-btn stories-edit-btn"
                            onClick={() => handleEditCollection(story.id, collection)}
                            title="Edit"
                          >
                            <Edit size={14} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="stories-action-btn stories-delete-btn"
                            onClick={() => openDeleteCollectionConfirm(story.id, collection.id)}
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="stories-page">
      <div className="stories-page-header">
        <div className="stories-page-title">
          <h1>Stories</h1>
          <p>Manage your app homepage stories and content</p>
        </div>
        <div className="stories-header-actions">
          {isReorderMode ? (
            <div className="stories-reorder-actions">
              <button
                className="stories-save-changes-btn"
                onClick={handleSaveChanges}
                disabled={!hasUnsavedChanges || isSaving}
              >
                {isSaving ? <Loader2 className="stories-spinner" size={20} /> : <Save size={20} />}
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                className="stories-exit-reorder-btn"
                onClick={toggleReorderMode}
                disabled={isSaving}
              >
                <X size={20} />
                Exit Reorder
              </button>
            </div>
          ) : (
            <div className="stories-normal-actions">
              {canEdit && (
                <button
                  className="stories-reorder-mode-btn"
                  onClick={toggleReorderMode}
                >
                  <GripVertical size={20} />
                  Reorder
                </button>
              )}
              {canCreate && (
                <button
                  className="stories-create-story-btn"
                  onClick={handleCreateStory}
                  disabled={isCreatingStory}
                >
                  {isCreatingStory ? <Loader2 className="stories-spinner" size={20} /> : <Plus size={20} />}
                  {isCreatingStory ? 'Creating...' : 'Create Story'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {isReorderMode && (
        <div className="stories-reorder-instructions">
          <p>💡 Use the arrow buttons to reorder stories. Click "Save Changes" when done.</p>
        </div>
      )}

      {error ? (
        <div className="stories-error-state">
          <p>{error}</p>
          <button onClick={handleRetryButton} className="stories-action-btn" style={{ marginTop: "10px" }}><RefreshCcw size={18} /></button>
        </div>
      ) : (
        <div className="stories-content">
          <div className="stories-story-group">
            <h2 className="stories-story-group-title">Active Stories ({activeCount})</h2>
            {isLoading ? (
              <div className="stories-loading-state">
                <div className="stories-loading-spinner"></div>
                <p>Loading Active Stories...</p>
              </div>
            ) : activeStories.length === 0 ? (
              <div className="stories-empty-state">No active stories found</div>
            ) : (
              <div className="stories-grid">
                {activeStories.map((story, index) => renderStoryCard(story, index, activeStories))}
              </div>
            )}
          </div>

          <div className="stories-story-group stories-expired-story-group">
            <div className="stories-expired-story-header" onClick={handleToggleExpired}>
              <h2 className="stories-story-group-title">Expired Stories ({expiredCount})</h2>
              <button className="stories-toggle-btn">
                {isExpiredExpanded ? <ArrowUpIcon size={20} /> : <ArrowDownIcon size={20} />}
              </button>
            </div>

            {isExpiredExpanded && (
              <>
                {isExpiredLoading ? (
                  <div className="stories-loading-state">
                    <div className="stories-loading-spinner"></div>
                    <p>Loading Expired Stories...</p>
                  </div>
                ) : expiredStories.length === 0 ? (
                  <div className="stories-empty-state">No expired stories found</div>
                ) : (
                  <>
                    <div className="stories-grid">
                      {expiredStories.map((story, index) => renderStoryCard(story, index, expiredStories))}
                    </div>

                    {(pagination.totalPages > 1 || pagination.hasNext || pagination.hasPrev) && (
                      <div className="stories-pagination">
                        <button
                          type="button"
                          onClick={handlePrevPage}
                          disabled={!pagination.hasPrev || isExpiredLoading}
                          className="stories-pagination-btn"
                        >
                          Previous
                        </button>
                        <span className="stories-pagination-info">
                          Page {pagination.currentPage} of {pagination.totalPages}
                          {pagination.totalCount > 0
                            ? ` · ${pagination.totalCount} expired`
                            : ''}
                        </span>
                        <button
                          type="button"
                          onClick={handleNextPage}
                          disabled={!pagination.hasNext || isExpiredLoading}
                          className="stories-pagination-btn"
                        >
                          {isExpiredLoading ? 'Loading...' : 'Next'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Story Dialog */}
      {showStoryDialog && (
        <div className="stories-dialog-overlay">
          <div className="stories-dialog stories-dialog--story">
            <div className="stories-dialog-header">
              <h2>{editingStory ? 'Edit Story' : 'Create New Story'}</h2>
              <button className="stories-dialog-close" onClick={handleCloseDialog}>
                <X size={20} />
              </button>
            </div>

            <div className="stories-dialog-content">
              <div className="stories-form-group">
                <label htmlFor="storyLabel">Story Label</label>
                <input
                  type="text"
                  id="storyLabel"
                  value={storyForm.label}
                  onChange={(e) => setStoryForm(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Enter story label"
                  className="stories-form-input"
                />
              </div>

              <div className="stories-form-group">
                <label>Expiry Date & Time</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <input
                    type="date"
                    value={storyForm.expiryDate}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setStoryForm(prev => ({ ...prev, expiryDate: e.target.value }))}
                    className="stories-form-input"
                    style={{ flex: 1 }}
                  />
                  <input
                    type="time"
                    value={storyForm.expiryTime}
                    min={storyForm.expiryDate === new Date().toISOString().split('T')[0]
                      ? new Date().toTimeString().slice(0, 5)
                      : undefined}
                    onChange={(e) => setStoryForm(prev => ({ ...prev, expiryTime: e.target.value }))}
                    className="stories-form-input"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="stories-form-group">
                <label htmlFor="storyThumbnail">Story Thumbnail</label>
                <div className="stories-image-upload-section">
                  <div className="stories-image-preview">
                    {storyForm.thumbnailPreview ? (
                      <div className="stories-image-preview-container">
                        <img src={storyForm.thumbnailPreview} alt="Thumbnail preview" />
                      </div>
                    ) : (
                      <div className="stories-image-placeholder">
                        <ImageIcon size={32} />
                        <span>No image selected</span>
                      </div>
                    )}
                  </div>

                  <div className="stories-image-upload-controls">
                    <input
                      type="file"
                      id="storyThumbnail"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="stories-file-input"
                    />
                    <label htmlFor="storyThumbnail" className="stories-upload-btn">
                      <Upload size={16} />
                      {storyForm.thumbnailPreview ? 'Change Image' : 'Choose Image'}
                    </label>
                  </div>
                  <div className="stories-file-size-limit">
                    Recommended size: 200px × 200px <br />
                    Maximum file size: 1 MB
                  </div>
                </div>
              </div>
            </div>

            <div className="stories-dialog-footer">
              <button className="stories-cancel-btn" onClick={handleCloseDialog}>
                Cancel
              </button>
              <button
                className="stories-save-btn"
                onClick={handleSaveStory}
                disabled={isCreatingStory || isUpdatingStory}
              >
                {isCreatingStory || isUpdatingStory ? (
                  <Loader2 className="stories-spinner" size={16} />
                ) : (
                  <Save size={16} />
                )}
                {isCreatingStory ? 'Creating...' : isUpdatingStory ? 'Updating...' : editingStory ? 'Update Story' : 'Create Story'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Dialog */}
      {showMediaDialog && (
        <div className="stories-dialog-overlay">
          <div className="stories-dialog stories-dialog--media">
            <div className="stories-dialog-header">
              <h2>{editingCollection ? 'Edit Collection' : 'Add Media Collection'}</h2>
              <button className="stories-dialog-close" onClick={handleCloseMediaDialog}>
                <X size={20} />
              </button>
            </div>

            <div className="stories-dialog-content">

              {/* Product Selection */}
              <div className="stories-form-group">
                <label>Product (Optional)</label>
                <div className="stories-choose-layout">
                  <button
                    type="button"
                    className="stories-choose-btn"
                    onClick={() => setIsProductDialogOpen(true)}
                  >
                    Choose Product
                  </button>
                  <div className="stories-selection-summary">
                    <span className="stories-products-count">
                      {mediaForm.productIds.length > 0
                        ? selectedProductsInfo[0]?.name
                          ? `${selectedProductsInfo[0].name} (ID: ${mediaForm.productIds[0]})`
                          : `Product ID: ${mediaForm.productIds[0]}`
                        : 'No product selected'}
                    </span>
                    {mediaForm.productIds.length > 0 && (
                      <button
                        type="button"
                        className="stories-view-btn"
                        onClick={() => setIsProductViewDialogOpen(true)}
                      >
                        View Product
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {!editingCollection && (
                <div className="stories-form-group">
                  <label htmlFor="mediaFile">Media File</label>
                  <div className="stories-image-upload-section">
                    <div className="stories-image-preview">
                      {mediaForm.preview ? (
                        <div className="stories-image-preview-container">
                          {mediaForm.type === 'video' ? (
                            <div className="stories-video-preview">
                              <Play size={32} />
                              <span>Video Preview</span>
                            </div>
                          ) : (
                            <img src={mediaForm.preview} alt="Media preview" />
                          )}
                        </div>
                      ) : (
                        <div className="stories-image-placeholder">
                          {mediaForm.type === 'video' ? <Video size={32} /> : <ImageIcon size={32} />}
                          <span>No media selected</span>
                        </div>
                      )}
                    </div>
                    <div className="stories-image-upload-controls">
                      <input
                        type="file"
                        id="mediaFile"
                        accept="image/*,video/*"
                        onChange={handleMediaChange}
                        className="stories-file-input"
                        disabled={compressingVideo}
                      />
                      <label htmlFor="mediaFile" className="stories-upload-btn">
                        {compressingVideo ? <Loader2 className="stories-spinner" size={16} /> : <Upload size={16} />}
                        {compressingVideo ? 'Compressing...' : mediaForm.preview ? 'Change Media' : 'Choose Media'}
                      </label>
                    </div>
                    <div className="stories-file-size-limit">
                      Recommended resolution: 1080 × 1920 pixels (9:16 ratio) <br />
                      Max size: Image 1MB | Video 6MB
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="stories-dialog-footer">
              <button className="stories-cancel-btn" onClick={handleCloseMediaDialog}>
                Cancel
              </button>
              <button
                className="stories-save-btn"
                onClick={handleSaveMedia}
                disabled={isAddingCollection || isUpdatingCollection || compressingVideo}
              >
                {isAddingCollection || isUpdatingCollection ? (
                  <Loader2 className="stories-spinner" size={16} />
                ) : (
                  <Save size={16} />
                )}
                {isAddingCollection ? 'Adding...' : isUpdatingCollection ? 'Updating...' : editingCollection ? 'Update Collection' : 'Add Media'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ProductSelectionModal
        isOpen={isProductDialogOpen}
        onClose={() => setIsProductDialogOpen(false)}
        selectedProductIds={mediaForm.productIds}
        onSave={handleProductSave}
        selectionIdKey="productId"
        maxSelections={1}
        title="Select Product"
        saveButtonLabel="Save Selection"
      />

      {isProductViewDialogOpen && (
        <div className="stories-dialog-overlay" onClick={() => setIsProductViewDialogOpen(false)}>
          <div className="stories-product-view-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="stories-product-view-header">
              <h2>Selected Product</h2>
              <button className="stories-dialog-close" onClick={() => setIsProductViewDialogOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="stories-product-view-content">
              {selectedProduct ? (
                <div className="stories-selected-product-item">
                  <div className="stories-selected-product-media">
                    {selectedProduct.images?.length > 0 ? (
                      <img src={selectedProduct.images[0].image} alt={selectedProduct.name} />
                    ) : selectedProduct.image ? (
                      <img src={selectedProduct.image} alt={selectedProduct.name} />
                    ) : (
                      <div className="stories-selected-product-no-image">No Image</div>
                    )}
                  </div>
                  <div className="stories-selected-product-details">
                    <span className="stories-selected-product-name">{selectedProduct.name}</span>
                    {selectedProduct.category && (
                      <span className="stories-selected-product-category">{selectedProduct.category}</span>
                    )}
                    <span className="stories-selected-product-id">ID: {mediaForm.productIds[0]}</span>
                  </div>
                  <button
                    type="button"
                    className="stories-remove-product-btn"
                    onClick={removeSelectedProduct}
                    title="Remove product"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ) : (
                <p className="stories-product-view-empty">No product details available</p>
              )}
            </div>
            <div className="stories-dialog-footer">
              <button className="stories-save-btn" onClick={() => setIsProductViewDialogOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      {showDeleteDialog && (
        <div className="stories-dialog-overlay">
          <div className="stories-dialog">
            <div className="stories-dialog-header">
              <h2>Confirm Delete</h2>
              <button className="stories-dialog-close" onClick={cancelDelete}>
                <X size={20} />
              </button>
            </div>

            <div className="stories-dialog-content">
              <p>
                {`Are you sure you want to delete this ${deleteType}?
                This action cannot be undone.`}
              </p>
            </div>

            <div className="stories-dialog-footer">
              <button
                className="stories-cancel-btn"
                onClick={cancelDelete}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="stories-delete-confirm-btn"
                onClick={confirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stories;
